import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, Navigate, useParams } from "react-router-dom";
import { Show, useAuth } from "@clerk/react";
import { ArrowLeft, Check, Download, LockKeyhole, ShieldAlert } from "lucide-react";
import { CertificateCard } from "@/components/CertificateCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Checklist({ items, completed, onToggle, saving }) {
  const pct = Math.round((completed.length / items.length) * 100);
  return <div className="lesson-card" data-testid="lesson-checklist">
    <div className="panel-head"><span>LAB CHECKLIST</span><span className="preview-label" data-testid="lesson-progress-text">{completed.length}/{items.length} · {pct}%{saving ? " · SAVING" : ""}</span></div>
    <div className="progress-bar"><i style={{ width: `${pct}%` }} data-testid="lesson-progress-bar"/></div>
    {items.map(it => { const on = completed.includes(it.id); return <button key={it.id} className={on ? "check-item on" : "check-item"} onClick={() => onToggle(it.id)} data-testid={`lesson-check-${it.id}`} aria-pressed={on}><span className="box">{on && <Check size={12}/>}</span>{it.text}</button>; })}
  </div>;
}

function Downloads({ lessonId, files, getToken }) {
  const get = async f => {
    const r = await axios.get(`${API}/premium/lessons/${lessonId}/downloads/${f.id}`, { headers: { Authorization: `Bearer ${await getToken()}` }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = f.name; a.click(); URL.revokeObjectURL(a.href);
  };
  return <div className="lesson-card" data-testid="lesson-downloads"><div className="panel-head"><span>DOWNLOADS</span><span>MEMBERS ONLY</span></div>
    {files.map(f => <button key={f.id} className="download-item" onClick={() => get(f)} data-testid={`lesson-download-${f.id}`}><Download size={14}/>{f.name}</button>)}
  </div>;
}

function Lesson() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => { const r = await axios.get(`${API}/premium/lessons/${id}`, { headers: { Authorization: `Bearer ${await getToken()}` } }); setLesson(r.data); })().catch(e => setError(e.response?.status || 500)); }, [id, getToken]);
  const toggle = useCallback(async itemId => {
    const completed = lesson.completed.includes(itemId) ? lesson.completed.filter(x => x !== itemId) : [...lesson.completed, itemId];
    setLesson(l => ({ ...l, completed })); setSaving(true);
    try { await axios.put(`${API}/premium/lessons/${id}/progress`, { completed }, { headers: { Authorization: `Bearer ${await getToken()}` } }); } finally { setSaving(false); }
  }, [lesson, id, getToken]);
  if (error === 403) return <div className="admin-denied" data-testid="lesson-locked"><LockKeyhole size={26}/><h2>Premium lesson</h2><p>An active membership is required. <Link to="/membership" className="text-link">Get membership →</Link></p></div>;
  if (error) return <div className="admin-denied" data-testid="lesson-not-found"><ShieldAlert size={26}/><h2>Lesson unavailable</h2><p>This lesson could not be loaded.</p></div>;
  if (!lesson) return <p className="admin-empty">Decrypting lesson...</p>;
  return <>
    <div className="page-intro compact"><div className="section-label"><span>{lesson.level.toUpperCase()}</span>{lesson.duration} · PREMIUM LESSON</div><h1 data-testid="lesson-title">{lesson.title}</h1><p>{lesson.summary}</p></div>
    <div className="lesson-layout">
      <div className="lesson-main">
        <div className="lesson-card"><div className="panel-head"><span>OBJECTIVES</span></div><ul className="objectives">{lesson.objectives.map(o => <li key={o}><Check size={14}/>{o}</li>)}</ul></div>
        {lesson.sections.map((s, i) => <section key={s.heading} className="lesson-section" data-testid={`lesson-section-${i + 1}`}><h2>{s.heading}</h2><p>{s.body}</p><ol>{s.steps.map(st => <li key={st}>{st}</li>)}</ol></section>)}
        <div className="notice-panel" data-testid="lesson-safety"><div className="notice-icon"><ShieldAlert size={20}/></div><div><h3>LEGAL & SAFETY</h3><p>{lesson.safety}</p></div></div>
      </div>
      <aside className="lesson-side"><Checklist items={lesson.checklist} completed={lesson.completed} onToggle={toggle} saving={saving}/><CertificateCard lessonId={lesson.id} done={lesson.completed.length} total={lesson.checklist.length} cert={lesson.certificate} onIssued={c => setLesson(l => ({ ...l, certificate: c }))} getToken={getToken}/><Downloads lessonId={lesson.id} files={lesson.downloads} getToken={getToken}/></aside>
    </div>
  </>;
}

export default function LessonPage() {
  return <section className="page-pad inner-page" data-testid="lesson-page">
    <Link to="/account" className="text-link back-link" data-testid="lesson-back-link"><ArrowLeft size={14}/> Back to console</Link>
    <Show when="signed-in"><Lesson/></Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace/></Show>
  </section>;
}
